import * as THREE from 'three';
import { View } from '../viewer/View';
import type { OrbitControllerLike } from '../viewer/RenderViewState';

/**
 * Minimal `OrbitControllerLike` for a widget RenderView that exists but is
 * not attached to a real canvas. Holds a persistent `View` (yaw/pitch/radius)
 * and accepts the same setter calls a `WidgetOrbitController` does, so any
 * code that drives the widget's state through `RenderViewState` keeps
 * working when the widget is closed.
 *
 * No DOM listeners, no smoothing, no RAF. `applyViewToCamera` writes pose
 * onto whatever camera reference is currently held — when nothing renders
 * from this camera, the write is harmless; when the widget re-attaches and
 * builds a real camera, the persisted `View` seeds the real orbit.
 */
export class HeadlessOrbitController implements OrbitControllerLike {
  readonly view: View;
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

  constructor() {
    this.view = new View();
    // Placeholder camera — nothing renders from it. Exists so `setCamera`
    // has something coherent to swap with when `applyCameraMode` runs while
    // detached (e.g. a tool sets a 2D view on the closed widget).
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 10000);
    this.camera.up.set(0, 0, 1);
  }

  setFromCamera(position: THREE.Vector3, target: THREE.Vector3): void {
    this.view.position.copy(position);
    const toTarget = new THREE.Vector3().subVectors(target, position);
    const radius = toTarget.length();
    if (radius > 1e-6) {
      this.view.direction = toTarget.normalize();
    }
    this.view.radius = Math.max(radius, 1e-3);
  }

  applyViewToCamera(): void {
    // Mirror WidgetOrbitController.applyToCamera's effect on `view.position`:
    // pose stays consistent on the held camera so a snapshot taken while
    // detached round-trips.
    this.camera.position.copy(this.view.position);
  }

  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    this.camera = camera;
  }

  setOrbitRadius(radius: number): void {
    this.view.radius = Math.max(1e-3, radius);
  }

  getOrbitRadius(): number {
    return this.view.radius;
  }
}
